import React, { useEffect, useMemo, useState } from "react";
import FormApi from "../../Api/FormApi";
import { FormDto } from "../../Models/FormDto";
import { FormFieldDto } from "../../Models/FormFieldDto";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../Reducers/store";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { toast } from "react-toastify";
import { TextInput } from "flowbite-react";
import Loader from "../Common/Loader";
import { UserLevelNames } from "../../Models/UserLevel";
import BoothPickerField from "./BoothPickerField";
import { UserData } from "../../Models/FormResponseDto";
import MemberApi from "../../Api/MemberApi";
import UserApi from "../../Api/UserApi";
import { User } from "../../Models/User";
import { Member } from "../../Models/Member";

type SubjectMode = "myself" | "other" | "new";

interface FormFillerPageProps {
  formId: number;
}
const FormFillerPage: React.FC<FormFillerPageProps> = ({ formId }) => {
  const { userModel } = useAppSelector((state) => state.user);
  const { id } = useParams();
  const actualFormId = formId ?? (id ? parseInt(id) : undefined);

  const [companyName, setCompanyName] = useState("");
  const [firstName, setFirstName] = useState(userModel?.firstName || "");
  const [lastName, setLastName] = useState(userModel?.lastName || "");
  const [email, setEmail] = useState(userModel?.emailAddress || "");
  const [phone, setPhone] = useState(userModel?.phoneNumber || "");

  const [form, setForm] = useState<FormDto | null>(null);
  type FieldValue = string | string[] | { start?: string; end?: string };
  const [values, setValues] = useState<{ [fieldId: number]: FieldValue }>({});
  const [canSubmit, setCanSubmit] = useState<boolean | null>(null); // null = loading
  const [isLoading, setIsLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>(
    {}
  );
  const [formErrors, setFormErrors] = useState<string[]>([]);

  // Track users for fields and field options
  const [fieldUsers, setFieldUsers] = useState<{
    [fieldId: number]: UserData[];
  }>({});
  const [optionUsers, setOptionUsers] = useState<{
    [optionKey: string]: UserData[];
  }>({});
  const [userCounts, setUserCounts] = useState<{ [optionKey: string]: number }>(
    {}
  );

  const isSuperAdmin = userModel?.userLevelId === UserLevelNames.SuperAdmin;
  const isMemberAdmin = userModel?.userLevelId === UserLevelNames.MemberAdmin;
  const isMemberUser = userModel?.userLevelId === UserLevelNames.MemberUser;
  const canPickSubject = !!(userModel && (isSuperAdmin || isMemberAdmin || isMemberUser));
  const canAddNewUser = !!(userModel && (isSuperAdmin || isMemberAdmin));

  const [subjectMode, setSubjectMode] = useState<SubjectMode>("myself");
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<number | "">("");
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [selectedSubjectUserId, setSelectedSubjectUserId] = useState<number | "">("");
  const [subjectAlreadySubmitted, setSubjectAlreadySubmitted] = useState(false);
  const [loadingCompanyUsers, setLoadingCompanyUsers] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
  });

  const contactReadOnly = canPickSubject && subjectMode !== "myself";

  const effectiveMemberId = useMemo(() => {
    if (isSuperAdmin) {
      return typeof selectedMemberId === "number" ? selectedMemberId : 0;
    }
    return userModel?.memberId ?? 0;
  }, [isSuperAdmin, selectedMemberId, userModel?.memberId]);

  const applyUserContact = (u: {
    firstName?: string | null;
    lastName?: string | null;
    emailAddress?: string | null;
    phoneNumber?: string | null;
  }) => {
    setFirstName(u.firstName || "");
    setLastName(u.lastName || "");
    setEmail(u.emailAddress || "");
    setPhone(u.phoneNumber || "");
  };

  const applyMyselfContact = () => {
    applyUserContact({
      firstName: userModel?.firstName,
      lastName: userModel?.lastName,
      emailAddress: userModel?.emailAddress,
      phoneNumber: userModel?.phoneNumber,
    });
  };

  useEffect(() => {
    FormApi.getForm(actualFormId).then((res) => {
      setForm(res.data);
    });
  }, [actualFormId]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    MemberApi.getMembers()
      .then((res) => setMembers(res.data || []))
      .catch(() => toast.error("Failed to load members."));
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!canPickSubject) return;
    if (subjectMode === "myself") {
      applyMyselfContact();
      setSelectedSubjectUserId(userModel?.id ?? "");
      return;
    }
    if (subjectMode === "new") {
      setFirstName(newUser.firstName);
      setLastName(newUser.lastName);
      setEmail(newUser.emailAddress);
      setPhone(newUser.phoneNumber);
      setSelectedSubjectUserId("");
    }
  }, [subjectMode, canPickSubject]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canPickSubject) return;
    if (subjectMode !== "other" && subjectMode !== "new") return;
    if (!effectiveMemberId) {
      setCompanyUsers([]);
      return;
    }

    setLoadingCompanyUsers(true);
    MemberApi.getMemberUserList(effectiveMemberId, {
      firstName: "",
      lastName: "",
      emailAddress: "",
    })
      .then((res) => {
        const list = (res.data || []).filter(
          (u) =>
            u.userLevelId !== 4 /* SocialGuest on server */ &&
            u.id !== undefined
        );
        setCompanyUsers(list);
      })
      .catch(() => toast.error("Failed to load company users."))
      .finally(() => setLoadingCompanyUsers(false));
  }, [canPickSubject, subjectMode, effectiveMemberId]);

  useEffect(() => {
    if (subjectMode !== "other" || !selectedSubjectUserId) {
      if (subjectMode === "other") setSubjectAlreadySubmitted(false);
      return;
    }
    const selected = companyUsers.find((u) => u.id === selectedSubjectUserId);
    if (selected) applyUserContact(selected);
  }, [selectedSubjectUserId, companyUsers, subjectMode]);

  useEffect(() => {
    const checkSubjectSubmitted = async () => {
      if (!actualFormId || !canPickSubject) {
        setSubjectAlreadySubmitted(false);
        return;
      }

      let subjectId = 0;
      if (subjectMode === "myself" && userModel?.id) subjectId = userModel.id;
      else if (subjectMode === "other" && typeof selectedSubjectUserId === "number")
        subjectId = selectedSubjectUserId;

      if (!subjectId) {
        setSubjectAlreadySubmitted(false);
        return;
      }

      try {
        const res = await FormApi.hasUserSubmittedForm(actualFormId, subjectId);
        setSubjectAlreadySubmitted(!!res.data);
      } catch {
        setSubjectAlreadySubmitted(false);
      }
    };
    checkSubjectSubmitted();
  }, [
    actualFormId,
    canPickSubject,
    subjectMode,
    selectedSubjectUserId,
    userModel?.id,
  ]);

  const navigate = useNavigate();
  const handleChange = (field: FormFieldDto, value: any) => {
    setValues((prev) => ({ ...prev, [field.fieldId!]: value }));
    const key = `field-${field.fieldId}`;
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const fieldAnchorId = (fieldId: number) => `form-field-${fieldId}`;

  const isFieldEmpty = (field: FormFieldDto, rawValue: FieldValue | undefined) => {
    if (field.fieldType === "Checkbox") {
      return !Array.isArray(rawValue) || rawValue.length === 0;
    }
    if (field.fieldType === "DateRange") {
      const range = rawValue as { start?: string; end?: string } | undefined;
      return !range?.start || !range?.end;
    }
    if (field.fieldType === "BoothPicker") {
      return typeof rawValue !== "string" || !rawValue.trim();
    }
    if (typeof rawValue === "string") {
      return !rawValue.trim();
    }
    return rawValue == null || rawValue === "";
  };

  const validateBeforeSubmit = (): boolean => {
    const errors: { [key: string]: string } = {};
    const summary: string[] = [];

    const requireContact = (
      key: string,
      label: string,
      value: string
    ) => {
      if (!value.trim()) {
        errors[key] = `${label} is required.`;
        summary.push(label);
      }
    };

    requireContact("contact-firstName", "First Name", firstName);
    requireContact("contact-lastName", "Last Name", lastName);
    requireContact("contact-email", "Email", email);

    if (canPickSubject) {
      if (isSuperAdmin && (subjectMode === "other" || subjectMode === "new") && !effectiveMemberId) {
        errors["subject-member"] = "Select a member company.";
        summary.push("Member company");
      }
      if (subjectMode === "other" && !selectedSubjectUserId) {
        errors["subject-user"] = "Select who this form is for.";
        summary.push("Person");
      }
      if (subjectMode === "new") {
        if (!newUser.firstName.trim() || !newUser.lastName.trim() || !newUser.emailAddress.trim()) {
          errors["subject-new"] = "First name, last name, and email are required for a new person.";
          summary.push("New person");
        }
      }
      if (subjectAlreadySubmitted && subjectMode !== "new") {
        errors["subject-dup"] = "This person has already submitted this form.";
        summary.push("Already submitted");
      }
    }

    for (const field of form?.fields ?? []) {
      if (
        !field.isRequired ||
        field.fieldType === "Section Title" ||
        field.fieldType === "Section Divider" ||
        field.fieldType === "Image Section"
      ) {
        continue;
      }

      const rawValue = values[field.fieldId!];
      if (!isFieldEmpty(field, rawValue)) continue;

      const label = (field.label || "This field").replace(/\*$/, "").trim();
      let message = `${label} is required.`;
      if (field.fieldType === "DateRange") {
        message = `Please enter ${label.toLowerCase().includes("arrival") ? "arrival and departure dates" : "both dates"} for "${label}".`;
      } else if (field.fieldType === "BoothPicker") {
        message = `Please select a booth for "${label}".`;
      } else if (field.fieldType === "Checkbox") {
        message = `Please select at least one option for "${label}".`;
      } else if (field.fieldType === "Radio" || field.fieldType === "Combo") {
        message = `Please select an option for "${label}".`;
      }

      errors[`field-${field.fieldId}`] = message;
      summary.push(label);
    }

    setFieldErrors(errors);
    setFormErrors(summary);

    if (summary.length === 0) {
      setFormErrors([]);
      return true;
    }

    toast.error(
      `Please complete ${summary.length} required field${summary.length === 1 ? "" : "s"} before submitting.`,
      { autoClose: 8000 }
    );

    const firstKey = Object.keys(errors)[0];
    window.setTimeout(() => {
      const el =
        document.getElementById(
          firstKey.startsWith("field-")
            ? fieldAnchorId(Number(firstKey.replace("field-", "")))
            : firstKey
        ) || document.getElementById("form-validation-summary");
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = el?.querySelector(
        "input, select, textarea, button"
      ) as HTMLElement | null;
      focusable?.focus?.();
    }, 50);

    return false;
  };

  const inputErrorClass = (key: string) =>
    fieldErrors[key] ? " border-rose-500 ring-1 ring-rose-400" : "";

  const fieldErrorText = (key: string) =>
    fieldErrors[key] ? (
      <p className="mt-1 text-sm text-rose-700 font-medium" role="alert">
        {fieldErrors[key]}
      </p>
    ) : null;

  useEffect(() => {
    const checkSubmission = async () => {
      try {
        // Logged-in member/admin users can always open the form so they can
        // register coworkers. Duplicate prevention is per subject on submit.
        if (canPickSubject) {
          setCanSubmit(true);
          setIsLoading(false);
          return;
        }

        if (userModel?.id && actualFormId) {
          const res = await FormApi.getSubmittedFormIdsByUser(userModel.id);
          const isAvailable = res.data.includes(Number(actualFormId));
          setCanSubmit(isAvailable);
        } else {
          // Public user — can always submit
          setCanSubmit(true);
        }
      } catch (error) {
        console.error("Error checking submission status:", error);
        setCanSubmit(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (actualFormId) checkSubmission();
  }, [actualFormId, userModel?.id, canPickSubject]);

  function getDateRangeTotal(
    start?: string,
    end?: string,
    pricePerDay = 0
  ): number {
    if (!start || !end) return 0;
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    const days = differenceInCalendarDays(endDate, startDate) + 1;
    return days > 0 ? days * pricePerDay : 0;
  }

  const calculateTotal = (): number => {
    let total = 0;

    form?.fields.forEach((field) => {
      const val = values[field.fieldId!];

      if (!val) return;

      if (field.fieldType === "Checkbox" && Array.isArray(val)) {
        val.forEach((v) => {
          const opt = field.options?.find((o) => o.value === v);
          if (opt) {
            total += Number(opt.amount) || 0;
            // Add user type pricing for this option
            if (opt.hasUsers) {
              const optionKey = `${field.fieldId}_${v}`;
              const users = optionUsers[optionKey] || [];
              users.forEach((user) => {
                total += user.price || 0;
              });
            }
          }
        });
      } else if (
        (field.fieldType === "Combo" || field.fieldType === "Radio") &&
        typeof val === "string"
      ) {
        const opt = field.options?.find((o) => o.value === val);
        if (opt) {
          total += Number(opt.amount) || 0;
          // Add user type pricing for this option
          if (opt.hasUsers) {
            const optionKey = `${field.fieldId}_${val}`;
            const users = optionUsers[optionKey] || [];
            users.forEach((user) => {
              total += user.price || 0;
            });
          }
        }
      } else if (
        field.fieldType === "DateRange" &&
        typeof val === "object" &&
        val !== null &&
        "start" in val &&
        "end" in val
      ) {
        const range = val as { start: string; end: string };
        const start = new Date(range.start);
        const end = new Date(range.end);
        const diffDays =
          Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) +
          1;
        total += (Number(field.price) || 0) * diffDays;
      }

      // Add field-level user type pricing
      if (field.hasUsers) {
        const users = fieldUsers[field.fieldId!] || [];
        users.forEach((user) => {
          total += user.price || 0;
        });
      }
    });

    return total;
  };

  const handleDateRangeChange = (
    fieldId: number,
    part: "start" | "end",
    value: string
  ) => {
    setValues((prev) => ({
      ...prev,
      [fieldId]: {
        ...((prev[fieldId] as { start?: string; end?: string }) || {}),
        [part]: value,
      },
    }));
  };

  function getSafeInputValue(
    value: FieldValue
  ): string | number | readonly string[] | undefined {
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      Array.isArray(value)
    )
      return value;
    return "";
  }

  // Handle updating field-level users
  const updateFieldUser = (
    fieldId: number,
    userIndex: number,
    prop: "firstName" | "lastName" | "userType",
    value: string,
    price?: number
  ) => {
    setFieldUsers((prev) => {
      const users = [...(prev[fieldId] || [])];
      users[userIndex] = {
        ...users[userIndex],
        [prop]: value,
        ...(prop === "userType" && price !== undefined ? { price } : {}),
      };
      return { ...prev, [fieldId]: users };
    });
  };

  // Handle updating option-level users
  const updateOptionUser = (
    optionKey: string,
    userIndex: number,
    prop: "firstName" | "lastName" | "userType",
    value: string,
    price?: number
  ) => {
    setOptionUsers((prev) => {
      const users = [...(prev[optionKey] || [])];
      users[userIndex] = {
        ...users[userIndex],
        [prop]: value,
        ...(prop === "userType" && price !== undefined ? { price } : {}),
      };
      return { ...prev, [optionKey]: users };
    });
  };

  // Initialize users array when needed
  const initializeFieldUsers = (fieldId: number, count: number) => {
    if (!fieldUsers[fieldId] || fieldUsers[fieldId].length !== count) {
      const users = Array(count)
        .fill(null)
        .map(() => ({ firstName: "", lastName: "", userType: "", price: 0 }));
      setFieldUsers((prev) => ({ ...prev, [fieldId]: users }));
    }
  };

  const initializeOptionUsers = (optionKey: string, count: number) => {
    if (!optionUsers[optionKey] || optionUsers[optionKey].length !== count) {
      const users = Array(count)
        .fill(null)
        .map(() => ({ firstName: "", lastName: "", userType: "", price: 0 }));
      setOptionUsers((prev) => ({ ...prev, [optionKey]: users }));
    }
  };

  // Render user input fields (reusable component)
  const renderUserInputs = (
    users: UserData[],
    userTypes: any[] | undefined,
    updateFn: (
      index: number,
      prop: string,
      value: string,
      price?: number
    ) => void
  ) => {
    const hasUserTypes = userTypes && userTypes.length > 0;
    return users.map((user, userIdx) => (
      <div
        key={userIdx}
        className="grid grid-cols-2 gap-2 p-3 bg-white rounded border"
      >
        {hasUserTypes && (
          <div className="col-span-2">
            <label className="block text-xs font-medium mb-1">
              Additional Person {userIdx + 1} - Type *
            </label>
            <select
              required
              className="w-full p-2 border rounded text-sm"
              value={user.userType || ""}
              onChange={(e) => {
                const selectedType = userTypes?.find(
                  (t: any) => t.typeName === e.target.value
                );
                updateFn(
                  userIdx,
                  "userType",
                  e.target.value,
                  selectedType?.price
                );
              }}
            >
              <option value="">-- Select Type --</option>
              {userTypes?.map((type: any, typeIdx: number) => (
                <option key={typeIdx} value={type.typeName}>
                  {type.typeName} - ${type.price.toFixed(2)}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium mb-1">
            {hasUserTypes ? "" : `Additional Person ${userIdx + 1} - `}First
            Name *
          </label>
          <input
            type="text"
            required
            className="w-full p-2 border rounded text-sm"
            value={user.firstName || ""}
            onChange={(e) => updateFn(userIdx, "firstName", e.target.value)}
            placeholder="First Name"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Last Name *</label>
          <input
            type="text"
            required
            className="w-full p-2 border rounded text-sm"
            value={user.lastName || ""}
            onChange={(e) => updateFn(userIdx, "lastName", e.target.value)}
            placeholder="Last Name"
          />
        </div>
      </div>
    ));
  };

  const handleSubmit = async () => {
    if (!validateBeforeSubmit()) {
      return;
    }

    setSubmitting(true);

    try {
      let subjectUserId = userModel ? userModel.id : 0;

      if (canPickSubject && userModel) {
        if (subjectMode === "myself") {
          subjectUserId = userModel.id;
        } else if (subjectMode === "other") {
          subjectUserId = Number(selectedSubjectUserId) || 0;
        } else if (subjectMode === "new") {
          if (!canAddNewUser) {
            toast.error("You do not have permission to add a new user.");
            setSubmitting(false);
            return;
          }
          if (!effectiveMemberId) {
            toast.error("Select a member company before adding a user.");
            setSubmitting(false);
            return;
          }
          setCreatingUser(true);
          const createPayload: User = {
            id: 0,
            memberId: effectiveMemberId,
            firstName: newUser.firstName.trim(),
            lastName: newUser.lastName.trim(),
            emailAddress: newUser.emailAddress.trim(),
            phoneNumber: newUser.phoneNumber.trim() || null,
            birthdate: null,
            userLevelId: UserLevelNames.MemberUser,
            isPrimary: false,
            address: "",
            city: "",
            state: "",
            zip: "",
            bio: "",
            imageId: null,
            image: null,
            member: null,
            userLevel: null,
            userFavorites: [],
            priority: null,
            femaAppPassword: "",
            femaAppUsername: "",
            femaLastSentDate: "",
          };
          const createRes = await UserApi.addUpdateUser(createPayload);
          setCreatingUser(false);
          if (!createRes.data.success) {
            toast.error(createRes.data.message || "Failed to create user.");
            setSubmitting(false);
            return;
          }
          subjectUserId = Number(createRes.data.affectedEntityIdentifier) || 0;
          if (!subjectUserId) {
            toast.error("User was created but no id was returned.");
            setSubmitting(false);
            return;
          }
        }
      }

      if (canPickSubject && !subjectUserId) {
        toast.error("Select who this form is for.");
        setSubmitting(false);
        return;
      }

      if (canPickSubject && subjectUserId && subjectMode !== "new") {
        const dup = await FormApi.hasUserSubmittedForm(actualFormId!, subjectUserId);
        if (dup.data) {
          toast.error("This person has already submitted this form.", {
            autoClose: 10000,
          });
          setSubjectAlreadySubmitted(true);
          setSubmitting(false);
          return;
        }
      }

    const answers =
      form?.fields.map((field) => {
        const rawValue = values[field.fieldId!];
        let value: string;
        let price = 0;
        let users: UserData[] | undefined = undefined;
        let answerOptionUsers:
          | { [optionValue: string]: UserData[] }
          | undefined = undefined;

        if (field.fieldType === "Checkbox" && Array.isArray(rawValue)) {
          value = rawValue.join(",");
          const tempOptionUsers: { [optionValue: string]: UserData[] } = {};
          for (const val of rawValue) {
            const opt = field.options?.find((o) => o.value === val);
            if (opt?.amount) price += opt.amount;
            // Collect users for this option if it has users enabled
            if (opt?.hasUsers) {
              const optionKey = `${field.fieldId}_${val}`;
              tempOptionUsers[val] = optionUsers[optionKey] || [];
            }
          }
          if (Object.keys(tempOptionUsers).length > 0) {
            answerOptionUsers = tempOptionUsers;
          }
        } else if (
          (field.fieldType === "Combo" || field.fieldType === "Radio") &&
          typeof rawValue === "string"
        ) {
          value = rawValue;
          const opt = field.options?.find((o) => o.value === rawValue);
          if (opt?.amount) price = opt.amount;
          // Collect users for this option if it has users enabled
          if (opt?.hasUsers && rawValue) {
            const optionKey = `${field.fieldId}_${rawValue}`;
            answerOptionUsers = {
              [rawValue]: optionUsers[optionKey] || [],
            };
          }
        } else if (
          field.fieldType === "DateRange" &&
          rawValue &&
          !Array.isArray(rawValue) &&
          typeof rawValue === "object" &&
          rawValue.start &&
          rawValue.end
        ) {
          const start = new Date(rawValue.start);
          const end = new Date(rawValue.end);
          const days =
            Math.ceil(
              (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
            ) + 1;
          value = `${rawValue.start}|${rawValue.end}`;
          price = (field.price || 0) * days;
        } else {
          value = typeof rawValue === "object" ? "" : rawValue || "";
        }

        // Collect field-level users if enabled
        if (field.hasUsers) {
          users = fieldUsers[field.fieldId!] || [];
        }

        return {
          fieldId: field.fieldId!,
          value,
          price,
          users,
          optionUsers: answerOptionUsers,
        };
      }) ?? [];

    const payload = {
      formId: actualFormId!,
      submittedById: subjectUserId,
      actingUserId: userModel ? userModel.id : 0,
      answers,
      userInfo: {
        firstName,
        lastName,
        email,
        phone,
        companyName,
      },
    };
      await FormApi.submitResponse(actualFormId!, payload);
      toast.success("Success!");
      if (userModel) {
        if (checkAdminAccess()) navigate("/forms/manage");
        else navigate("/forms/thank-you");
      } else navigate("/submitform/thank-you");
    } catch (err: any) {
      console.error(err);
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Unknown error";
      if (status === 409) {
        toast.error(msg, { autoClose: 10000 });
      } else {
        toast.error("Error saving form: " + msg, { autoClose: 10000 });
      }
    } finally {
      setCreatingUser(false);
      setSubmitting(false);
    }
  };

  function checkAdminAccess() {
    if (userModel && userModel.userLevelId === UserLevelNames.SuperAdmin) {
      return true;
    } else {
      return false;
    }
  }

  if (!form) return <Loader />;
  if (isLoading || canSubmit === null) return <Loader />;

  if (canSubmit == false) {
    return (
      <div className="border border-gray-300 bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="p-6 text-center text-green-700 text-lg font-semibold">
          ✅ Thank you! You have already submitted this form.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mb-6 p-4 border bg-mainBackground rounded-lg shadow">
      <div className="border border-gray-300 bg-white rounded-lg shadow-md p-6 mb-6 max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-4">{form.title}</h1>
        <h5 className="mb-4">{form.introDescription}</h5>

        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="mb-6 p-4 border bg-mainBackground rounded-lg shadow">
            <label className="block mb-1 text-2xl font-bold">
              Your Information
            </label>
            <label className="block mb-1 italic">
              Please review your information below. If you need to make changes,
              please do so before submitting. This information will be used in
              all the event marketing and communication including your name
              badges and company signeage.
            </label>

            {canPickSubject && (
              <div className="mt-4 mb-4 p-3 border rounded bg-white space-y-3">
                <label className="block text-lg font-semibold">
                  Who is this form for?
                </label>
                <p className="text-sm text-gray-600">
                  Choose yourself, another person at the company, or add a new
                  person. Selecting someone else will not change their profile
                  from these contact fields.
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="subjectMode"
                      checked={subjectMode === "myself"}
                      onChange={() => setSubjectMode("myself")}
                    />
                    Myself
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="subjectMode"
                      checked={subjectMode === "other"}
                      onChange={() => setSubjectMode("other")}
                    />
                    Another person at the company
                  </label>
                  {canAddNewUser && (
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="subjectMode"
                        checked={subjectMode === "new"}
                        onChange={() => setSubjectMode("new")}
                      />
                      Add new person
                    </label>
                  )}
                </div>

                {isSuperAdmin && (subjectMode === "other" || subjectMode === "new") && (
                  <div id="subject-member">
                    <label className="block text-sm font-bold mb-1">
                      Member company *
                    </label>
                    <select
                      className={`w-full p-2 border rounded${inputErrorClass("subject-member")}`}
                      value={selectedMemberId}
                      onChange={(e) => {
                        const val = e.target.value ? Number(e.target.value) : "";
                        setSelectedMemberId(val);
                        setSelectedSubjectUserId("");
                      }}
                    >
                      <option value="">Select member…</option>
                      {members.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                    {fieldErrorText("subject-member")}
                  </div>
                )}

                {subjectMode === "other" && (
                  <div id="subject-user">
                    <label className="block text-sm font-bold mb-1">
                      Person *
                    </label>
                    <select
                      className={`w-full p-2 border rounded${inputErrorClass("subject-user")}`}
                      value={selectedSubjectUserId}
                      disabled={loadingCompanyUsers || !effectiveMemberId}
                      onChange={(e) =>
                        setSelectedSubjectUserId(
                          e.target.value ? Number(e.target.value) : ""
                        )
                      }
                    >
                      <option value="">
                        {loadingCompanyUsers
                          ? "Loading…"
                          : !effectiveMemberId
                            ? "Select a member first…"
                            : "Select person…"}
                      </option>
                      {companyUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {(u.firstName || "") + " " + (u.lastName || "")}{" "}
                          {u.emailAddress ? `(${u.emailAddress})` : ""}
                        </option>
                      ))}
                    </select>
                    {fieldErrorText("subject-user")}
                  </div>
                )}

                {subjectMode === "new" && canAddNewUser && (
                  <div id="subject-new" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-bold mb-1">
                        First Name *
                      </label>
                      <input
                        className="w-full p-2 border rounded"
                        value={newUser.firstName}
                        onChange={(e) => {
                          const v = e.target.value;
                          setNewUser((p) => ({ ...p, firstName: v }));
                          setFirstName(v);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-1">
                        Last Name *
                      </label>
                      <input
                        className="w-full p-2 border rounded"
                        value={newUser.lastName}
                        onChange={(e) => {
                          const v = e.target.value;
                          setNewUser((p) => ({ ...p, lastName: v }));
                          setLastName(v);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        className="w-full p-2 border rounded"
                        value={newUser.emailAddress}
                        onChange={(e) => {
                          const v = e.target.value;
                          setNewUser((p) => ({ ...p, emailAddress: v }));
                          setEmail(v);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-1">
                        Phone
                      </label>
                      <input
                        className="w-full p-2 border rounded"
                        value={newUser.phoneNumber}
                        onChange={(e) => {
                          const v = e.target.value;
                          setNewUser((p) => ({ ...p, phoneNumber: v }));
                          setPhone(v);
                        }}
                      />
                    </div>
                    {fieldErrorText("subject-new")}
                  </div>
                )}

                {subjectAlreadySubmitted && (
                  <p className="text-sm text-rose-700 font-medium" role="alert">
                    This person has already submitted this form. Choose someone
                    else to continue.
                  </p>
                )}
                {fieldErrorText("subject-dup")}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              <div id="contact-firstName">
                <label
                  htmlFor="firstName"
                  className="block text-sm text-gray-700 mb-1 font-bold"
                >
                  First Name *
                </label>
                <input
                  id="firstName"
                  type="text"
                  readOnly={contactReadOnly && subjectMode !== "new"}
                  className={`w-full p-2 border rounded${inputErrorClass("contact-firstName")}${
                    contactReadOnly && subjectMode !== "new" ? " bg-gray-100" : ""
                  }`}
                  value={firstName}
                  onChange={(e) => {
                    if (contactReadOnly && subjectMode !== "new") return;
                    setFirstName(e.target.value);
                    setFieldErrors((prev) => {
                      if (!prev["contact-firstName"]) return prev;
                      const next = { ...prev };
                      delete next["contact-firstName"];
                      return next;
                    });
                  }}
                />
                {fieldErrorText("contact-firstName")}
              </div>
              <div id="contact-lastName">
                <label
                  htmlFor="lastName"
                  className="block text-sm text-gray-700 mb-1 font-bold"
                >
                  Last Name *
                </label>
                <input
                  id="lastName"
                  type="text"
                  readOnly={contactReadOnly && subjectMode !== "new"}
                  className={`w-full p-2 border rounded${inputErrorClass("contact-lastName")}${
                    contactReadOnly && subjectMode !== "new" ? " bg-gray-100" : ""
                  }`}
                  value={lastName}
                  onChange={(e) => {
                    if (contactReadOnly && subjectMode !== "new") return;
                    setLastName(e.target.value);
                    setFieldErrors((prev) => {
                      if (!prev["contact-lastName"]) return prev;
                      const next = { ...prev };
                      delete next["contact-lastName"];
                      return next;
                    });
                  }}
                />
                {fieldErrorText("contact-lastName")}
              </div>
              <div id="contact-email">
                <label
                  htmlFor="email"
                  className="block text-sm text-gray-700 mb-1 font-bold"
                >
                  Email *
                </label>
                <input
                  id="email"
                  type="email"
                  readOnly={contactReadOnly && subjectMode !== "new"}
                  className={`w-full p-2 border rounded${inputErrorClass("contact-email")}${
                    contactReadOnly && subjectMode !== "new" ? " bg-gray-100" : ""
                  }`}
                  value={email}
                  onChange={(e) => {
                    if (contactReadOnly && subjectMode !== "new") return;
                    setEmail(e.target.value);
                    setFieldErrors((prev) => {
                      if (!prev["contact-email"]) return prev;
                      const next = { ...prev };
                      delete next["contact-email"];
                      return next;
                    });
                  }}
                />
                {fieldErrorText("contact-email")}
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm text-gray-700 mb-1 font-bold"
                >
                  Phone
                </label>
                <TextInput
                  id="phone"
                  readOnly={contactReadOnly && subjectMode !== "new"}
                  value={phone}
                  onChange={(e) => {
                    if (contactReadOnly && subjectMode !== "new") return;
                    setPhone(e.target.value);
                  }}
                  placeholder="Phone"
                />
              </div>
              {!userModel && (
                <div>
                  <label
                    htmlFor="companyName"
                    className="block text-sm text-gray-700 mb-1 font-bold"
                  >
                    Company Name
                  </label>
                  <TextInput
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Company Name"
                  />
                </div>
              )}
            </div>
          </div>
          {form.fields
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((field) => (
              <div
                key={field.fieldId}
                id={fieldAnchorId(field.fieldId!)}
                className={
                  field.fieldType === "Section Divider" ||
                  field.fieldType === "Section Title"
                    ? "mb-6"
                    : `mb-6 p-4 border rounded-lg shadow-sm${
                        fieldErrors[`field-${field.fieldId}`]
                          ? " border-rose-500 bg-rose-50"
                          : " border-gray-300"
                      }`
                }
              >
                {field.fieldType === "Section Title" && (
                  <>
                    <label className="block mb-1 text-2xl font-bold">
                      {field.label}
                      {field.isRequired ? " *" : ""}
                    </label>
                    {field.description && field.description?.length > 0 && (
                      <label
                        className="block mb-1 italic"
                        dangerouslySetInnerHTML={{
                          __html: (field.description || "").replace(
                            /\n/g,
                            "<br/>"
                          ),
                        }}
                      ></label>
                    )}
                  </>
                )}
                {field.fieldType !== "Section Title" && (
                  <>
                    <label className="block mb-1 font-semibold">
                      {field.label}
                      {field.isRequired ? " *" : ""}
                    </label>
                    {field.description && field.description?.length > 0 && (
                      <label
                        className="block mb-1"
                        dangerouslySetInnerHTML={{
                          __html: (field.description || "").replace(
                            /\n/g,
                            "<br/>"
                          ),
                        }}
                      ></label>
                    )}
                  </>
                )}
                {field.fieldType === "Section Divider" && <hr></hr>}
                {field.fieldType === "Image Section" && field.defaultValue && (
                  <>
                    {field.label && (
                      <p className="text-center italic text-sm text-gray-600 mb-2">
                        {field.label}
                      </p>
                    )}
                    <img
                      src={field.defaultValue}
                      alt={field.label || "Image"}
                      className="max-w-full mb-4 rounded shadow"
                    />
                  </>
                )}

                {field.fieldType === "BoothPicker" && actualFormId && (
                  <BoothPickerField
                    formId={actualFormId}
                    field={field}
                    value={
                      typeof values[field.fieldId!] === "string"
                        ? values[field.fieldId!]
                        : ""
                    }
                    onChange={(booth) => handleChange(field, booth)}
                    required={field.isRequired}
                  />
                )}

                {field.fieldType === "SingleText" && (
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    required={field.isRequired}
                    placeholder={field.label || ""}
                    value={getSafeInputValue(values[field.fieldId!])}
                    onChange={(e) => handleChange(field, e.target.value)}
                  />
                )}
                {field.fieldType === "MultiLineText" && (
                  <textarea
                    className="w-full p-2 border rounded"
                    required={field.isRequired}
                    placeholder={field.label || ""}
                    value={getSafeInputValue(values[field.fieldId!])}
                    onChange={(e) => handleChange(field, e.target.value)}
                  />
                )}
                {field.fieldType === "Date" && (
                  <input
                    type="date"
                    className="w-full p-2 border rounded"
                    required={field.isRequired}
                    value={getSafeInputValue(values[field.fieldId!])}
                    onChange={(e) => handleChange(field, e.target.value)}
                  />
                )}
                {field.fieldType === "Time" && (
                  <input
                    type="time"
                    className="w-full p-2 border rounded"
                    required={field.isRequired}
                    value={getSafeInputValue(values[field.fieldId!])}
                    onChange={(e) => handleChange(field, e.target.value)}
                  />
                )}
                {field.fieldType === "Combo" && (
                  <div>
                    <select
                      className="w-full p-2 border rounded"
                      required={field.isRequired}
                      value={getSafeInputValue(values[field.fieldId!])}
                      onChange={(e) => handleChange(field, e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      {field.options?.map((opt, idx) => (
                        <option key={idx} value={opt.value}>
                          {opt.name}
                          {opt.amount && opt.amount > 0
                            ? ` - $${opt.amount.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}`
                            : ""}
                        </option>
                      ))}
                    </select>

                    {/* Show user input fields for selected combo option if it requires users */}
                    {values[field.fieldId!] &&
                      field.options?.map((opt) => {
                        if (
                          values[field.fieldId!] !== opt.value ||
                          !opt.hasUsers
                        )
                          return null;

                        const optionKey = `${field.fieldId}_${opt.value}`;
                        const count =
                          opt.userAddType === "Variable"
                            ? userCounts[optionKey] || 1
                            : opt.userAddCount || 1;
                        initializeOptionUsers(optionKey, count);

                        return (
                          <div
                            key={opt.value}
                            className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded"
                          >
                            {opt.userAddType === "Variable" && (
                              <div className="mb-3">
                                <label className="block text-sm font-medium mb-1">
                                  How many additional people (in addition to
                                  yourself)?
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  max={opt.userAddCount || 10}
                                  className="p-2 border rounded w-32"
                                  value={userCounts[optionKey] || 1}
                                  onChange={(e) => {
                                    const count = parseInt(e.target.value) || 1;
                                    setUserCounts((prev) => ({
                                      ...prev,
                                      [optionKey]: count,
                                    }));
                                    initializeOptionUsers(optionKey, count);
                                  }}
                                />
                              </div>
                            )}

                            <div className="space-y-3">
                              <p className="text-sm font-medium text-gray-700">
                                Please provide information for additional
                                attendees (in addition to yourself):
                              </p>
                              {renderUserInputs(
                                optionUsers[optionKey] || [],
                                opt.userTypes,
                                (idx, prop, val, price) =>
                                  updateOptionUser(
                                    optionKey,
                                    idx,
                                    prop as any,
                                    val,
                                    price
                                  )
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
                {field.fieldType === "Radio" && (
                  <div className="space-y-3">
                    {field.options?.map((opt, idx) => {
                      const isSelected = values[field.fieldId!] === opt.value;
                      const optionKey = `${field.fieldId}_${opt.value}`;

                      // Initialize users when option is selected
                      if (isSelected && opt.hasUsers) {
                        const count =
                          opt.userAddType === "Variable"
                            ? userCounts[optionKey] || 1
                            : opt.userAddCount || 1;
                        initializeOptionUsers(optionKey, count);
                      }

                      return (
                        <div key={idx}>
                          <label className="block">
                            <input
                              type="radio"
                              name={`field-${field.fieldId}`}
                              value={opt.value}
                              required={field.isRequired}
                              checked={isSelected}
                              onChange={() => handleChange(field, opt.value)}
                              className="mr-2"
                            />
                            {opt.name}
                            {opt.amount && opt.amount > 0
                              ? ` - $${opt.amount.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                })}`
                              : ""}
                          </label>

                          {/* Show user input fields if this option is selected and requires users */}
                          {isSelected && opt.hasUsers && (
                            <div className="ml-6 mt-3 p-4 bg-blue-50 border border-blue-200 rounded">
                              {opt.userAddType === "Variable" && (
                                <div className="mb-3">
                                  <label className="block text-sm font-medium mb-1">
                                    How many additional people (in addition to
                                    yourself)?
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    max={opt.userAddCount || 10}
                                    className="p-2 border rounded w-32"
                                    value={userCounts[optionKey] || 1}
                                    onChange={(e) => {
                                      const count =
                                        parseInt(e.target.value) || 1;
                                      setUserCounts((prev) => ({
                                        ...prev,
                                        [optionKey]: count,
                                      }));
                                      initializeOptionUsers(optionKey, count);
                                    }}
                                  />
                                </div>
                              )}

                              <div className="space-y-3">
                                <p className="text-sm font-medium text-gray-700">
                                  Please provide information for additional
                                  attendees (in addition to yourself):
                                </p>
                                {renderUserInputs(
                                  optionUsers[optionKey] || [],
                                  opt.userTypes,
                                  (idx, prop, val, price) =>
                                    updateOptionUser(
                                      optionKey,
                                      idx,
                                      prop as any,
                                      val,
                                      price
                                    )
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {field.fieldType === "Checkbox" && (
                  <div className="space-y-3">
                    {field.options?.map((opt, idx) => {
                      const checked = Array.isArray(values[field.fieldId!])
                        ? (values[field.fieldId!] as string[]).includes(
                            opt.value
                          )
                        : false;
                      const optionKey = `${field.fieldId}_${opt.value}`;

                      // Initialize users when option is checked
                      if (checked && opt.hasUsers) {
                        const count =
                          opt.userAddType === "Variable"
                            ? userCounts[optionKey] || 1
                            : opt.userAddCount || 1;
                        initializeOptionUsers(optionKey, count);
                      }

                      return (
                        <div key={idx}>
                          <label className="block">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const current = Array.isArray(
                                  values[field.fieldId!]
                                )
                                  ? [...(values[field.fieldId!] as string[])]
                                  : [];
                                if (e.target.checked) current.push(opt.value);
                                else
                                  current.splice(current.indexOf(opt.value), 1);
                                handleChange(field, current);
                              }}
                              className="mr-2"
                            />
                            {opt.name}
                            {opt.amount && opt.amount > 0
                              ? ` - $${opt.amount.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                })}`
                              : ""}
                          </label>

                          {/* Show user input fields if this option is checked and requires users */}
                          {checked && opt.hasUsers && (
                            <div className="ml-6 mt-3 p-4 bg-blue-50 border border-blue-200 rounded">
                              {opt.userAddType === "Variable" && (
                                <div className="mb-3">
                                  <label className="block text-sm font-medium mb-1">
                                    How many additional people (in addition to
                                    yourself)?
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    max={opt.userAddCount || 10}
                                    className="p-2 border rounded w-32"
                                    value={userCounts[optionKey] || 1}
                                    onChange={(e) => {
                                      const count =
                                        parseInt(e.target.value) || 1;
                                      setUserCounts((prev) => ({
                                        ...prev,
                                        [optionKey]: count,
                                      }));
                                      initializeOptionUsers(optionKey, count);
                                    }}
                                  />
                                </div>
                              )}

                              <div className="space-y-3">
                                <p className="text-sm font-medium text-gray-700">
                                  Please provide information for additional
                                  attendees (in addition to yourself):
                                </p>
                                {renderUserInputs(
                                  optionUsers[optionKey] || [],
                                  opt.userTypes,
                                  (idx, prop, val, price) =>
                                    updateOptionUser(
                                      optionKey,
                                      idx,
                                      prop as any,
                                      val,
                                      price
                                    )
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {field.fieldType === "DateRange" && (
                  <div className="space-y-2">
                    <div className="flex space-x-2">
                      <input
                        type="date"
                        value={(values[field.fieldId!] as any)?.start || ""}
                        required={field.isRequired}
                        onChange={(e) => {
                          const prevRange =
                            (values[field.fieldId!] as any) || {};
                          const start = e.target.value;
                          // Pull the end date along so the end calendar doesn't
                          // linger on an earlier month than the new start date.
                          // ISO yyyy-MM-dd strings compare correctly as text.
                          const end =
                            !prevRange.end || prevRange.end < start
                              ? start
                              : prevRange.end;
                          handleChange(field, { ...prevRange, start, end });
                        }}
                        className="p-2 border rounded"
                      />
                      <input
                        type="date"
                        value={(values[field.fieldId!] as any)?.end || ""}
                        required={field.isRequired}
                        min={(values[field.fieldId!] as any)?.start || undefined}
                        onChange={(e) =>
                          handleChange(field, {
                            ...((values[field.fieldId!] as any) || {}),
                            end: e.target.value,
                          })
                        }
                        className="p-2 border rounded"
                      />
                    </div>
                    <div className="text-sm text-gray-600">
                      {(() => {
                        const v = values[field.fieldId!] as any;
                        const total = getDateRangeTotal(
                          v?.start,
                          v?.end,
                          field.price || 0
                        );
                        return total > 0
                          ? `Total: ${new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "USD",
                            }).format(total)}`
                          : "";
                      })()}
                    </div>
                  </div>
                )}

                {/* Field-level user collection (for fields without options that need user info) */}
                {field.hasUsers &&
                  field.fieldType !== "Combo" &&
                  field.fieldType !== "Radio" &&
                  field.fieldType !== "Checkbox" &&
                  field.fieldType !== "BoothPicker" && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded">
                      {(() => {
                        const count = field.userAddCount || 1;
                        initializeFieldUsers(field.fieldId!, count);
                        return (
                          <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-700">
                              Please provide information for {count} additional{" "}
                              {count === 1 ? "person" : "people"} (in addition
                              to yourself):
                            </p>
                            {renderUserInputs(
                              fieldUsers[field.fieldId!] || [],
                              field.userTypes,
                              (idx, prop, val, price) =>
                                updateFieldUser(
                                  field.fieldId!,
                                  idx,
                                  prop as any,
                                  val,
                                  price
                                )
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                {fieldErrorText(`field-${field.fieldId}`)}
              </div>
            ))}
          <div className="mt-6 text-right text-lg font-semibold">
            Total:
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(calculateTotal())}
          </div>

          {formErrors.length > 0 && (
            <div
              id="form-validation-summary"
              className="mt-4 mb-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900"
              role="alert"
            >
              <div className="font-semibold mb-1">
                Please complete the required fields below before submitting:
              </div>
              <ul className="list-disc pl-5 space-y-1">
                {formErrors.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
          )}

          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
            disabled={submitting || creatingUser || subjectAlreadySubmitted}
          >
            {creatingUser
              ? "Creating user…"
              : submitting
                ? "Submitting…"
                : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FormFillerPage;
